import React, { useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NotFound from "../../components/not-found/NotFound";
import Pagination from "react-js-pagination";
import ModalBox from "../../components/modal/ModalBox";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { toastMessage } from "../../utils/toastMessage";
import { dateFormatWithTime } from "../../utils/dateFormat";
import { charLimit } from "../../utils/charLimit";
import findSerialNumber from "../../utils/findSerialNumber";
import { CommonSelect, Input, TextArea } from "../../components/Form";
import DescriptionModel from "../../components/imagePreview/DescriptionModel";
import common from "../../routes/common";
import { 
  getScheduledNotificationsApi, 
  addScheduledNotificationApi, 
  cancelScheduledNotificationApi 
} from "../../services/notificationManagement";

const { NOT_FOUND } = common;

const getDisplayStatus = (notification) => {
  const scheduledAt = new Date(notification?.scheduledAt);

  if (
    notification?.status === "scheduled" &&
    !Number.isNaN(scheduledAt.getTime()) &&
    scheduledAt <= new Date()
  ) {
    return "sent";
  }

  return notification?.status || "---";
};

const ScheduledNotification = () => {
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState(false);
  const [description, setDescription] = useState({});
  const [selectedId, setSelectedId] = useState("");
  const [buttonRef, buttonLoader] = useButtonLoader("Yes");
  const [inputField, setInputField] = useState({});
  const [error, setError] = useState({});
  const [userTypeValue, setUserTypeValue] = useState("");
  const [gender, setGender] = useState("");

  const userTypeOptions = [
    { label: "All", value: "all" },
    { label: "User", value: "user" },
    { label: "Consultant", value: "cons" },
  ];

  const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Others", value: "others" },
  ];

  useEffect(() => {
    fetchScheduledNotifications();
  }, []);

  const fetchScheduledNotifications = async () => {
    try {
      const sendData = {
        limit: "10",
        page: activePage
      };
      
      const res = await getScheduledNotificationsApi(sendData);
      console.log("Scheduled notifications response:", res);
      
      if (res.data.code === 200) {
        setScheduledNotifications(res?.data?.result?.list || res?.data?.result || []);
        setTotal(res?.data?.result?.total || 0);
      } else {
        setScheduledNotifications([]);
        setTotal(0);
        toastMessage(res.data.message, "error", "scheduledNotifications");
      }
    } catch (error) {
      console.log("error", error);
      setScheduledNotifications([]);
      setTotal(0);
    }
  };

  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber);
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setInputField({ ...inputField, [name]: value });
  };

  const validation = () => {
    let status = true;
    const errors = {};
    
    if (!inputField?.message) {
      errors["message"] = "Message is mandatory";
      status = false;
    }
    if (!inputField?.activeUserType) {
      errors["activeUserType"] = "Choose user type";
      status = false;
    }
    if (!userTypeValue) {
      errors["userType"] = "Select user type";
      status = false;
    }
    if (!inputField?.scheduledAt) {
      errors["scheduledAt"] = "Schedule time is mandatory";
      status = false;
    } else {
      // datetime-local is IST wall-clock; compare against IST "now"
      const scheduledTime = new Date(`${inputField.scheduledAt}:00+05:30`);
      const currentTime = new Date();
      if (Number.isNaN(scheduledTime.getTime()) || scheduledTime <= currentTime) {
        errors["scheduledAt"] = "Schedule time must be in the future";
        status = false;
      }
    }
    
    setError(errors);
    return status;
  };

  const addScheduledNotification = async () => {
    const status = validation();
    if (!status) return;

    try {
      buttonLoader(true);
      
      const reqData = { 
        ...inputField, 
        userType: userTypeValue 
      };
      // Send explicit IST offset so UTC servers don't misread datetime-local as UTC
      if (reqData.scheduledAt && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(reqData.scheduledAt)) {
        reqData.scheduledAt = `${reqData.scheduledAt}:00+05:30`;
      }
      if (gender) reqData.gender = gender;

      console.log("Adding scheduled notification with data:", reqData);
      const { data } = await addScheduledNotificationApi(reqData);
      console.log("Add scheduled notification response:", data);

      if (data.code === 200) {
        toastMessage("Scheduled notification added successfully", "success");
        setAddModal(false);
        setInputField({});
        setUserTypeValue("");
        setGender("");
        fetchScheduledNotifications(); // Refresh the list
      } else {
        toastMessage(data.message, "error", "addScheduledNotification");
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to add scheduled notification", "error");
    } finally {
      buttonLoader(false);
    }
  };

  const cancelScheduledNotification = async () => {
    try {
      buttonLoader(true);
      
      console.log("Cancelling notification with ID:", selectedId);
      const res = await cancelScheduledNotificationApi({
        notificationId: selectedId
      });
      console.log("Cancel scheduled notification response:", res);

      if (res?.data?.code === 200) {
        toastMessage("Scheduled notification cancelled successfully", "success");
        setDeleteModal(false);
        fetchScheduledNotifications(); // Refresh the list
      } else {
        toastMessage(res?.data?.message, "error", "cancelScheduledNotification");
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to cancel scheduled notification", "error");
    } finally {
      buttonLoader(false);
    }
  };

  const onClose = () => {
    setDescription(null);
    setDescriptionModal(false);
  };

  return (
    <>
      <div className="block_user_button" style={{ marginBottom: '20px' }}>
        <button onClick={() => setAddModal(true)}>
          <AddIcon />
          Add Scheduled Notification
        </button>
      </div>

      <div className="content-table">
        <table>
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Title</th>
              <th>Message</th>
              <th>User Type</th>
              <th>Source</th>
              <th>Scheduled At</th>
              <th>Status</th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {scheduledNotifications.length > 0 ? (
              scheduledNotifications.map((notification, index) => (
                <tr key={notification._id}>
                  <td>{findSerialNumber(index, activePage, 10)}</td>
                  <td>{notification.title || "---"}</td>
                  <td
                    onClick={() => {
                      if (notification.message && notification.message.length > 50) {
                        setDescriptionModal(true);
                        setDescription({
                          message: notification.message,
                          title: notification.title,
                        });
                      }
                    }}
                  >
                    {notification.message && notification.message.length > 50
                      ? charLimit(notification.message, 50)
                      : notification.message || "---"}
                  </td>
                  <td>
                    {notification.userType === "cons"
                      ? "Consultant"
                      : notification.userType === "all"
                      ? "All"
                      : "User"}
                    {notification.notificationType === "targeted_scheduled" ? (
                      <div style={{ fontSize: "11px", color: "#888" }}>
                        Targeted ({notification.recipientCount || 0})
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {notification?.source === "claude_ops_chat" ? (
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: "4px",
                          backgroundColor: "#e3f2fd",
                          color: "#1565c0",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}
                        title="Created via Claude Ops Chat"
                      >
                        Claude Ops
                      </span>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#666" }}>Admin</span>
                    )}
                  </td>
                  <td>{dateFormatWithTime(notification.scheduledAt)}</td>
                  <td>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: "#ffeb3b",
                        color: "#000",
                        fontSize: "12px",
                        fontWeight: "bold"
                      }}
                    >
                      {getDisplayStatus(notification)}
                    </span>
                  </td>
                  <td>
                    <div className="action_button">
                                             <IconButton
                         aria-label="cancel"
                         className="px-2 py-0"
                         onClick={() => {
                           console.log("Setting selected ID:", notification._id);
                           setDeleteModal(true);
                           setSelectedId(notification._id);
                         }}
                         title="Cancel Scheduled Notification"
                       >
                        <DeleteIcon className="edit_icon" />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <NotFound
                cols={8}
                msg="Scheduled notifications not found!"
                icon={NOT_FOUND}
              />
            )}
          </tbody>
        </table>
      </div>

      {total >= 10 && (
        <div className="Pagination">
          <Pagination
            activePage={activePage}
            previousLabel={"previous"}
            nextLabel={"next"}
            itemsCountPerPage={10}
            totalItemsCount={total}
            pageRangeDisplayed={5}
            onChange={handlePageChange}
          />
        </div>
      )}

      {/* Add Scheduled Notification Modal */}
      {addModal && (
        <ModalBox
          status={addModal}
          onClose={() => setAddModal(false)}
          title={"Add Scheduled Notification"}
          size={"sm"}
          closeIcon
          custom
          customizedFooter={
            <div className="row">
              <div className="col-12">
                <div className="form-group mb-2">
                  <div>
                    <label className="my-1">Select user type</label>
                    <span className="text-danger"> *</span>
                  </div>
                  <div className="checkbox_Wrap">
                    <label>
                      <span>All</span>{" "}
                      <input
                        type="radio"
                        name="activeUserType"
                        value="all"
                        checked={inputField?.activeUserType === "all"}
                        onChange={onChangeHandler}
                      />
                    </label>
                    <label>
                      <span>Active</span>
                      <input
                        type="radio"
                        name="activeUserType"
                        value="active"
                        checked={inputField?.activeUserType === "active"}
                        onChange={onChangeHandler}
                      />
                    </label>
                    <label>
                      <span>In-active</span>
                      <input
                        type="radio"
                        name="activeUserType"
                        value="inactive"
                        checked={inputField?.activeUserType === "inactive"}
                        onChange={onChangeHandler}
                      />
                    </label>
                  </div>
                  {error.activeUserType && (
                    <div className="invalid-feedback d-block f-2">
                      {error.activeUserType}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group mb-2">
                  <Input
                    type="text"
                    name="title"
                    value={inputField?.title || ""}
                    onChange={onChangeHandler}
                    placeholder="Title"
                    className="form-control"
                    label={"Title"}
                  />
                </div>
              </div>
              {/* <div className="col-12">
                <div className="form-group select_sm_width">
                  <CommonSelect
                    className="add-consultant"
                    label={"Gender"}
                    options={genderOptions}
                    onChange={setGender}
                  />
                </div>
              </div> */}
              <div className="col-12">
                <div className="form-group select_sm_width">
                  <CommonSelect
                    className="add-consultant"
                    label={"User Type"}
                    options={userTypeOptions}
                    onChange={setUserTypeValue}
                    required
                    error={!userTypeValue && error.userType}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group mb-2">
                  <Input
                    type="datetime-local"
                    name="scheduledAt"
                    value={inputField?.scheduledAt || ""}
                    onChange={onChangeHandler}
                    className={`form-control ${error.scheduledAt ? 'is-invalid' : ''}`}
                    label={"Schedule Time"}
                    required
                    min={new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 16)}
                  />
                  {error.scheduledAt && (
                    <div className="invalid-feedback d-block">
                      {error.scheduledAt}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="form-group mb-2">
                  <TextArea
                    type="text"
                    name="message"
                    value={inputField?.message || ""}
                    required
                    onChange={onChangeHandler}
                    placeholder="Message"
                    className={`form-control ${error.message ? 'is-invalid' : ''}`}
                    label={"Message"}
                    error={!inputField?.message && error.message}
                  />
                </div>
              </div>
              <div className="text-center">
                <button
                  className="btn_size text-center"
                  onClick={addScheduledNotification}
                  ref={buttonRef}
                >
                  Schedule Notification
                </button>
              </div>
            </div>
          }
        />
      )}

      {/* Cancel Scheduled Notification Modal */}
      <ModalBox
        status={deleteModal}
        onClose={() => setDeleteModal(false)}
        title={"Cancel Scheduled Notification"}
        description={"Are you sure you want to cancel this scheduled notification?"}
        action={cancelScheduledNotification}
        ref={buttonRef}
      />

      {/* Description Modal */}
      <DescriptionModel
        show={descriptionModal}
        close={onClose}
        description={description?.message}
        title={description?.title}
      />
    </>
  );
};

export default ScheduledNotification; 
